import React from 'react'
import { useTranslation } from 'react-i18next'
import Delete from '@mui/icons-material/Delete'
import Add from '@mui/icons-material/Add'
import moment from 'moment'
import { TimePicker } from '@carsys/form'
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { IWorkHours, Times } from '@cbgms/api'

interface IOuterProps {
  push: (times: { BeginTime: string; EndTime: string }) => void
  remove: (index: number) => void
}

export const calculateBreakDurations = (times?: Times[]) => {
  if (!times) {
    return []
  }

  return times.map(item => {
    if (!item.BeginTime || !item.EndTime) {
      return '00:00'
    }

    const momentEndTime = moment(`${item.EndTime}`, 'HH:mm')
    const momentBeginTime = moment(`${item.BeginTime}`, 'HH:mm')

    if (!momentEndTime.isValid() || !momentBeginTime.isValid()) {
      return '00:00'
    }

    const duration = momentEndTime.diff(momentBeginTime)
    const formattedDuration = moment.utc(duration).format('HH:mm')

    return formattedDuration
  })
}

export const Break: React.FC<IOuterProps> = props => {
  const { push, remove } = props
  const { values } = useFormikContext<IWorkHours>()
  const { t } = useTranslation()

  const fields = values.BreakTimes.Times
  const breakTimesDuration = calculateBreakDurations(values.BreakTimes.Times)

  return (
    <Grid container spacing={2}>
      {fields &&
        fields.map((_breakTime, key) => (
          <Grid key={`breakTime-${key}`} item xs={3}>
            <Card data-testid='break-card'>
              <CardHeader
                title={<Typography variant='subtitle2'>{t('setting_opening_hour.break', { number: key + 1 })}</Typography>}
                action={
                  <IconButton data-testid='remove-break-button' onClick={() => remove(key)} size='large'>
                    <Tooltip title={`${t('setting_opening_hour.remove_break')}`}>
                      <Delete />
                    </Tooltip>
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TimePicker
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: t('setting_opening_hour.start_time')
                        }
                      }}
                      minutesStep={5}
                      name={`BreakTimes.Times[${key}].BeginTime`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TimePicker
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: t('setting_opening_hour.end_time')
                        }
                      }}
                      minutesStep={5}
                      name={`BreakTimes.Times[${key}].EndTime`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      size='small'
                      value={breakTimesDuration[key]}
                      inputProps={{ maxLength: 6, readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      <Grid item xs={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 2 }}>
          <Button
            data-testid='add-break-button'
            variant='contained'
            color='primary'
            startIcon={<Add />}
            onClick={() => push({ BeginTime: '00:00', EndTime: '00:00' })}
          >
            {t('setting_opening_hour.add_new_break')}
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}

export default Break
